import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowLeft, CalendarPlus, Plus, X } from 'lucide-react';
import { Report } from '../../types';
import { SifaFrist } from '../../services/sifa/betreuungTypes';
import { deleteFrist } from '../../services/sifa/sifaBetreuungService';
import { asaVorschlag, fristAmpel } from '../../services/sifa/fristenLogic';
import { fetchMitarbeiter } from '../../services/sifa/sifaService';
import { describeError } from '../../services/errorMessage';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useFristen } from './useFristen';
import { FristenListe } from './FristenListe';
import { FristDialog, FristErledigenDialog } from './FristDialog';
import { formatDatum, heuteIso } from './unterweisungModulLogic';
import { FILTER_VORGABE, FristAmpel, FristEntwurf, FristenFilter, entwurfAusVorschlag, zaehleFristen, zeigeAsaHinweis } from './fristenModulLogic';

interface Props {
  project: { id: string; name?: string; data: Report };
  onBack: () => void;
  isReadOnly?: boolean;
}

/**
 * Faelligkeiten & Vorsorge eines SiFa-Projekts — duenner Container.
 *
 * Fristenregister (sifa_fristen) mit Ampel, Anlegen/Bearbeiten, „Erledigt"
 * mit Datum (legt bei Wiederholung die naechste Zeile an) und Loeschen mit
 * Rueckfrage. Beim ersten Oeffnen ohne Fristen und mehr als 20
 * Beschaeftigten steht der ASA-Hinweis (§ 11 ASiG) mit fertigem Vorschlag.
 * Kein onUpdate: dieses Modul schreibt nie ins Projekt-JSON.
 */
export const FristenModul: React.FC<Props> = ({ project, onBack, isReadOnly = false }) => {
  const isMobile = useIsMobile();
  const meta = project.data?.metadata;
  const beschaeftigte = meta?.sifa?.beschaeftigte;
  const { fristen, laden, fehler, uebernehmen, entfernen } = useFristen(project.id);

  const [filter, setFilter] = useState<FristenFilter>(FILTER_VORGABE);
  const [dialog, setDialog] = useState<{ offen: boolean; frist: SifaFrist | null; vorlage: FristEntwurf | null }>({ offen: false, frist: null, vorlage: null });
  const [erledigen, setErledigen] = useState<SifaFrist | null>(null);
  const [asaWeg, setAsaWeg] = useState(false);
  const [namen, setNamen] = useState<Record<string, string>>({});
  const [meldung, setMeldung] = useState<string | null>(null);
  const [meldungTon, setMeldungTon] = useState<'info' | 'fehler'>('info');

  const zeigeFehler = (text: string) => { setMeldungTon('fehler'); setMeldung(text); };
  const zeigeInfo = (text: string) => { setMeldungTon('info'); setMeldung(text); };

  // Ein Datum je Render genuegt: die Ampel haengt am Tag, nicht an der Sekunde.
  const heute = useMemo(() => new Date(), []);
  const ampel = (f: SifaFrist): FristAmpel => fristAmpel(f, heute);
  const zaehler = zaehleFristen(fristen, ampel);

  // Namen fuer die Spalte „Person" — nur wenn ueberhaupt eine Frist an einer
  // Person haengt; fail-open, ohne Stamm bleibt die Spalte leer.
  const brauchtNamen = fristen.some((f) => !!f.mitarbeiterId);
  useEffect(() => {
    if (!brauchtNamen) return;
    let aktiv = true;
    fetchMitarbeiter(project.id)
      .then((m) => { if (aktiv) setNamen(Object.fromEntries(m.map((x) => [x.id, `${x.nachname}, ${x.vorname}`]))); })
      .catch(() => { /* Spalte bleibt leer */ });
    return () => { aktiv = false; };
  }, [project.id, brauchtNamen]);
  const personName = (id: string | null) => (id ? namen[id] || null : null);

  const asaAnlegen = () => {
    const v = asaVorschlag(beschaeftigte ?? 0, heute);
    if (!v) return;
    setDialog({ offen: true, frist: null, vorlage: entwurfAusVorschlag(v, heuteIso(heute)) });
  };

  const loeschen = async (f: SifaFrist) => {
    if (isReadOnly) return;
    const ok = window.confirm(`Frist „${f.titel}" (fällig ${formatDatum(f.faelligAm)}) löschen?\n\nDie Zeile verschwindet aus dem Register. Das lässt sich nicht rückgängig machen.`);
    if (!ok) return;
    try {
      await deleteFrist(f.id);
      entfernen(f.id);
      zeigeInfo(`„${f.titel}" gelöscht.`);
    } catch (e) {
      zeigeFehler(describeError(e));
    }
  };

  const nachErledigt = (erledigt: SifaFrist, naechste: SifaFrist | null) => {
    uebernehmen(erledigt);
    if (naechste) uebernehmen(naechste);
    setErledigen(null);
    zeigeInfo(naechste
      ? `„${erledigt.titel}" erledigt. Nächste Fälligkeit angelegt: ${formatDatum(naechste.faelligAm)}.`
      : `„${erledigt.titel}" erledigt.`);
  };

  const dialogSchliessen = () => setDialog({ offen: false, frist: null, vorlage: null });

  return (
    <div data-glass-view="sifa" className="flex flex-col h-full bg-slate-50">
      <div data-glass="kopf" className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 shrink-0">
        <button onClick={onBack} className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-slate-100 rounded-full transition-colors">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-bold text-slate-800 truncate">Fälligkeiten &amp; Vorsorge</h1>
          <p className="text-xs text-slate-500 truncate">
            {project.name || meta?.projectName} · {zaehler.offen} offen
            {zaehler.ueberfaellig > 0 && <span className="text-red-600 font-bold"> · {zaehler.ueberfaellig} überfällig</span>}
          </p>
        </div>
        {!isReadOnly && (
          <button onClick={() => setDialog({ offen: true, frist: null, vorlage: null })} className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-xl">
            <Plus className="w-4 h-4" /> Anlegen
          </button>
        )}
      </div>

      <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${isMobile ? 'pb-20' : ''}`}>
        {isReadOnly && (
          <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-900">
            Nur-Ansicht: Dieses Projekt ist schreibgeschützt geöffnet. Fristen lassen sich ansehen — anlegen, erledigen und löschen ist gesperrt.
          </div>
        )}
        {(meldung || fehler) && (
          <div className={`p-3 rounded-xl text-xs flex items-start gap-2 border ${meldungTon === 'fehler' || fehler ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
            {(meldungTon === 'fehler' || fehler) && <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />}
            <span className="flex-1">{meldung || fehler}</span>
            {meldung && <button onClick={() => setMeldung(null)} className="p-1"><X className="w-3.5 h-3.5" /></button>}
          </div>
        )}

        {!isReadOnly && !asaWeg && zeigeAsaHinweis(fristen, beschaeftigte, laden) && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3">
            <CalendarPlus className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-amber-900">ASA-Sitzung vierteljährlich (§ 11 ASiG)</p>
              <p className="text-xs text-amber-800 mt-0.5">
                Der Betrieb hat {beschaeftigte} Beschäftigte — ab 20 ist ein Arbeitsschutzausschuss Pflicht, der mindestens einmal im Quartal tagt. Als erste Frist anlegen?
              </p>
              <div className="flex gap-2 mt-2">
                <button onClick={asaAnlegen} className="min-h-[36px] px-3 text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-lg">Frist anlegen</button>
                <button onClick={() => setAsaWeg(true)} className="min-h-[36px] px-3 text-xs font-semibold text-amber-900 hover:bg-amber-100 rounded-lg">Später</button>
              </div>
            </div>
          </div>
        )}

        <FristenListe
          fristen={fristen}
          laden={laden}
          filter={filter}
          onFilter={setFilter}
          ampel={ampel}
          personName={personName}
          isReadOnly={isReadOnly}
          isMobile={isMobile}
          onBearbeiten={(f) => setDialog({ offen: true, frist: f, vorlage: null })}
          onErledigen={setErledigen}
          onLoeschen={(f) => void loeschen(f)}
        />
      </div>

      {dialog.offen && (
        <FristDialog
          key={dialog.frist?.id || 'neu'}
          projectId={project.id}
          frist={dialog.frist}
          vorlage={dialog.vorlage}
          isReadOnly={isReadOnly}
          onClose={dialogSchliessen}
          onGespeichert={(f) => { uebernehmen(f); dialogSchliessen(); zeigeInfo(`„${f.titel}" gespeichert.`); }}
        />
      )}

      {erledigen && (
        <FristErledigenDialog
          frist={erledigen}
          onClose={() => setErledigen(null)}
          onErledigt={nachErledigt}
        />
      )}
    </div>
  );
};
